import React, { createContext, useContext, useState, useMemo } from 'react';
import { createFeature } from '@edreamsodigeo/frontend-core';
import { ThreeColumnsItineraryType } from '@components/Results/Components/ThreeColumnsItinerary/types';
import { useItinerariesWithPriceResponseQuery } from 'clientState/providers/ItinerariesPricesProvider';
import { useSearchId } from 'clientState/providers/SearchResponseProvider';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';
import {
  hasCarriers,
  getCarriers,
  getSearchRequest,
  getValuePropositionList,
} from './helper';
import { Money, ItinerarySearchAlternativesRequest } from 'generated/graphql';
import {
  AlternativeItineraryContextType,
  AlternativeItineraryActionsContextType,
  AlternativeItineraryValuePropositionType,
  AlternativeItineraryCalculatorProviderProps,
} from './types';
import { useAlternativeItineraryMutation } from 'clientState/providers/AlternativeItineraryMutationProvider';
import {
  ALTERNATIVE_FLIGHT_NON_BOOKABLE,
  DOUBLE_CARD_NO_ISSUANCE_ALTERNATIVE_FLIGHTS_RESULTS,
} from 'clientState/ab/aliases';
import useAlternativeFlight from 'clientState/hooks/useAlternativeFlight';
import { getItineraryInformation } from 'clientState/hooks/useCreateShoppingCart/useCreateShoppingCart';
import { cloneDeep } from 'lodash';
import { useUnbundle } from 'clientState/providers/SearchResponseProvider/SearchItinerariesResponseProvider';

const applyDiscount = (itineraryData, alternativeDiscounted) => {
  const alternativeWithDiscountApplied = cloneDeep(itineraryData);
  alternativeWithDiscountApplied.fees = [
    ...itineraryData.fees.filter(
      (fee) =>
        !alternativeDiscounted.fees.some((altFee) => altFee.type === fee.type)
    ),
    ...alternativeDiscounted.fees,
  ];

  alternativeWithDiscountApplied.price = alternativeDiscounted.fees.find(
    (fee) => fee.type === 'MEMBER_PRICE_POLICY_DISCOUNTED'
  ).price;
  alternativeWithDiscountApplied.perks = alternativeDiscounted.perks;
  return alternativeWithDiscountApplied;
};

const AlternativeItineraryContext =
  createContext<AlternativeItineraryContextType>({
    selectedItinerary: undefined,
    alternativeItinerary: undefined,
    alternativeItineraryPosition: undefined,
    valueProposition: 'social',
    valuePropositions: [],
    alternativeItineraryPrice: undefined,
    additionalInformation: {},
    discountAmount: undefined,
    loading: false,
    errors: null,
    continueWithErrorBookeable: false,
    altSearchId: undefined,
  });
const AlternativeItineraryActionsContext =
  createContext<AlternativeItineraryActionsContextType>({
    requestAlternativeItinerary: () => {},
    hasAlternativeItineraryCarrier: () => false,
    addItineraryToUnavailableList: () => {},
    isItineraryUnavailable: () => false,
    setContinueWithErrorBookeableState: () => {},
  });

const AlternativeItineraryCalculatorProvider: React.FC<
  AlternativeItineraryCalculatorProviderProps
> = ({ withDiscount = false, children }) => {
  const [alternativeItinerary, setAlternativeItinerary] =
    useState<ThreeColumnsItineraryType>();
  const [selectedItinerary, setSelectedItinerary] =
    useState<ThreeColumnsItineraryType>();
  const [previousSelectedItinerary, setPreviousSelectedItinerary] =
    useState<ThreeColumnsItineraryType>();
  const [valuePropositions, setValuePropositions] = useState<
    AlternativeItineraryValuePropositionType[]
  >([]);
  const [valueProposition, setValueProposition] =
    useState<AlternativeItineraryValuePropositionType>('social');
  const [additionalInformation, setAdditionalInformation] = useState<{
    airport?: number;
  }>({});
  const [alternativeItineraryPosition, setAlternativeItineraryPosition] =
    useState<number>();
  const [alternativeItineraryPrice, setAlternativeItineraryPrice] =
    useState<number>();
  const [altSearchId, setAltSearchId] = useState<number>();
  const [discountAmount, setDiscountAmount] = useState<Money>();
  const [continueWithErrorBookeable, setContinueWithErrorBookeable] =
    useState<boolean>(false);
  const { data: itinerariesData } = useItinerariesWithPriceResponseQuery();
  const { alternativeFlight, setAlternativeFlight } = useAlternativeFlight();
  const [unavailableItinerariesList, setUnavailableItinerariesList] = useState<
    string[]
  >([]);
  const searchId = useSearchId();
  const searchRequest = usePerformedSearchRequest();

  const {
    mutate,
    alternativeItineraryId,
    alternativeItineraries,
    loading,
    errors,
    alternativeSearchId,
    alternativeItineraryObject,
    setAlternativeMutationLaunched,
  } = useAlternativeItineraryMutation();

  const [isUnbundle] = useUnbundle();

  let itineraryObj;
  let itineraryPosition = 0;

  if (
    alternativeItineraryId &&
    alternativeSearchId &&
    (alternativeSearchId !== altSearchId ||
      previousSelectedItinerary?.id !== selectedItinerary?.id)
  ) {
    if (!isUnbundle) {
      const alternativeDiscounted = alternativeItineraries?.find(
        (itinerary) => itinerary.id === alternativeItineraryId
      );

      const itineraryData = itinerariesData?.itineraries?.find(
        (itinerary) => itinerary.id === alternativeItineraryId
      );

      itineraryObj = applyDiscount(itineraryData, alternativeDiscounted);
      itineraryPosition = itinerariesData?.itineraries?.findIndex(
        (itinerary) => itinerary.id === alternativeItineraryId
      );
    } else {
      itineraryObj = alternativeItineraryObject;
    }

    const { valuePropositions, additionalInformation } =
      getValuePropositionList(
        searchRequest?.itinerary.segments,
        selectedItinerary,
        itineraryObj,
        withDiscount
      );
    const alternativeItineraryPrice = itineraryObj?.price?.amount;
    const discount = itineraryObj?.perks?.alternativeItineraryDiscount;
    const alternativeFlight = {
      alternativeItinerary: itineraryObj,
      itineraryPosition,
      originalItineraryId: selectedItinerary?.id,
      selectedItinerary,
      valueProposition,
    };

    setPreviousSelectedItinerary(selectedItinerary);
    setAlternativeItinerary(itineraryObj);
    setAlternativeFlight(alternativeFlight);
    setAlternativeItineraryPosition(itineraryPosition);
    setValueProposition(valuePropositions[0]);
    setValuePropositions(valuePropositions);
    setAdditionalInformation(additionalInformation);
    setAlternativeItineraryPrice(alternativeItineraryPrice);
    setDiscountAmount(discount);
    setAltSearchId(alternativeSearchId);
  }

  const actions = useMemo(
    () => ({
      requestAlternativeItinerary: (itinerary: ThreeColumnsItineraryType) => {
        setAlternativeMutationLaunched(true);
        const selectedItinerary = itinerary;
        setSelectedItinerary(selectedItinerary);
        const mutationRequest: ItinerarySearchAlternativesRequest = {
          eligibleForDiscount: withDiscount,
          itineraryCarriers: getCarriers(selectedItinerary),
          itineraryId: selectedItinerary.key || selectedItinerary.id || '',
          searchId: searchId || 0,
          searchRequest: getSearchRequest(searchRequest),
        };
        mutate({
          itinerarySearchAlternativesRequest: mutationRequest,
        });
      },
      hasAlternativeItineraryCarrier: (
        itinerary: ThreeColumnsItineraryType
      ) => {
        return hasCarriers(itinerary);
      },
      addItineraryToUnavailableList: (itinerary: ThreeColumnsItineraryType) => {
        if (
          hasCarriers(itinerary) &&
          itinerary?.id &&
          !unavailableItinerariesList.includes(itinerary.id)
        ) {
          setUnavailableItinerariesList([
            ...unavailableItinerariesList,
            itinerary.id,
          ]);
        }
      },
      isItineraryUnavailable: (itinerary: ThreeColumnsItineraryType) => {
        const itineraryInformation = getItineraryInformation(itinerary);
        const isInDisabledItineraries =
          alternativeFlight?.disabledItineraries?.some(
            (disabledItinerary) =>
              itineraryInformation.flightNumbers.join('_') === disabledItinerary
          );
        return itinerary.id
          ? unavailableItinerariesList.includes(itinerary.id) ||
              isInDisabledItineraries
          : false;
      },
      setContinueWithErrorBookeableState: (
        continueWithErrorBookeable: boolean
      ) => {
        setContinueWithErrorBookeable(continueWithErrorBookeable);
      },
    }),
    [
      setAlternativeMutationLaunched,
      withDiscount,
      searchId,
      searchRequest,
      mutate,
      unavailableItinerariesList,
      alternativeFlight?.disabledItineraries,
    ]
  );
  return (
    <AlternativeItineraryContext.Provider
      value={{
        selectedItinerary,
        alternativeItinerary,
        alternativeItineraryPosition,
        valueProposition,
        valuePropositions,
        additionalInformation,
        alternativeItineraryPrice,
        discountAmount,
        loading,
        errors,
        continueWithErrorBookeable,
        altSearchId,
      }}
    >
      <AlternativeItineraryActionsContext.Provider value={actions}>
        {children}
      </AlternativeItineraryActionsContext.Provider>
    </AlternativeItineraryContext.Provider>
  );
};

export const useAlternativeItinerary = (): AlternativeItineraryContextType =>
  useContext(AlternativeItineraryContext);

export const useAlternativeItineraryActions =
  (): AlternativeItineraryActionsContextType =>
    useContext(AlternativeItineraryActionsContext);

const AlternativeItineraryCalculatorProviderWithDiscount: React.FC<
  AlternativeItineraryCalculatorProviderProps
> = (props) => (
  <AlternativeItineraryCalculatorProvider {...props} withDiscount={true} />
);

const AlternativeItineraryNoIssuanceDesktopWithDiscountOld = createFeature({
  name: DOUBLE_CARD_NO_ISSUANCE_ALTERNATIVE_FLIGHTS_RESULTS,
  variations: [
    AlternativeItineraryCalculatorProvider,
    AlternativeItineraryCalculatorProviderWithDiscount,
    AlternativeItineraryCalculatorProviderWithDiscount,
    AlternativeItineraryCalculatorProviderWithDiscount,
  ],
  version: 1,
});

const AlternativeItineraryNoIssuanceDesktopWithDiscount = createFeature({
  name: ALTERNATIVE_FLIGHT_NON_BOOKABLE,
  variations: [
    AlternativeItineraryNoIssuanceDesktopWithDiscountOld,
    AlternativeItineraryCalculatorProviderWithDiscount,
  ],
  version: 1,
});

export default AlternativeItineraryNoIssuanceDesktopWithDiscount;
